import { useLoader } from '@react-three/fiber';
import React, { FC } from 'react';
import { DoubleSide, RepeatWrapping, Texture, TextureLoader } from 'three';

import { CanopyColor } from '../../configurations/colors';
import { usePergola } from '../../data-stores/pergola';

export const CanopyMaterial: FC = () => {
  const canopyColor = usePergola(s => s.canopy.color);

  const doveWhiteTexture = useLoader(TextureLoader, [
    '/3d-models/textures/canopy/canopy_white_diffuse.jpg?v=2',
    '/3d-models/textures/canopy/canopy_normal.jpg?v=2',
    '/3d-models/textures/canopy/canopy_roughness.jpg?v=2',
  ]);

  const cappuccinoTexture = useLoader(TextureLoader, [
    '/3d-models/textures/canopy/canopy_diffuse_2.jpg?v=2',
    '/3d-models/textures/canopy/canopy_normal.jpg?v=2',
    '/3d-models/textures/canopy/canopy_roughness.jpg?v=2',
  ]);

  const sterlingSilverTexture = useLoader(TextureLoader, [
    '/3d-models/textures/canopy/canopy_diffuse_3.jpg?v=2',
    '/3d-models/textures/canopy/canopy_normal.jpg?v=2',
    '/3d-models/textures/canopy/canopy_roughness.jpg?v=2',
  ]);

  const desertSandTexture = useLoader(TextureLoader, [
    '/3d-models/textures/canopy/canopy_diffuse_4.jpg?v=2',
    '/3d-models/textures/canopy/canopy_normal.jpg?v=2',
    '/3d-models/textures/canopy/canopy_roughness.jpg?v=2',
  ]);

  const pacificBlueTexture = useLoader(TextureLoader, [
    '/3d-models/textures/canopy/Texture_2_diffuse.jpg?v=2',
    '/3d-models/textures/canopy/Texture_2_normal.jpg?v=2',
    '/3d-models/textures/canopy/Texture_2_roughness.jpg?v=2',
  ]);

  const cloudNineTexture = useLoader(TextureLoader, [
    '/3d-models/textures/canopy/Texture_1_diffuse.jpg?v=2',
    '/3d-models/textures/canopy/Texture_1_normal.jpg?v=2',
    '/3d-models/textures/canopy/Texture_1_roughness.jpg?v=2',
  ]);

  let textures: Texture[];

  switch (canopyColor) {
    case CanopyColor.CAPPUCCINO:
      textures = cappuccinoTexture;
      break;
    case CanopyColor.DOVE_WHITE:
      textures = doveWhiteTexture;
      break;
    case CanopyColor.STERLING_SILVER:
      textures = sterlingSilverTexture;
      break;
    case CanopyColor.DESERT_SAND:
      textures = desertSandTexture;
      break;
    case CanopyColor.PACIFIC_BLUE:
      textures = pacificBlueTexture;
      break;
    case CanopyColor.CLOUD_NINE:
      textures = cloudNineTexture;
      break;
    default:
      textures = cappuccinoTexture;
  }

  for (const texture of textures) {
    if (texture) {
      texture.wrapS = RepeatWrapping;
      texture.wrapT = RepeatWrapping;
      if (canopyColor === CanopyColor.PACIFIC_BLUE || canopyColor === CanopyColor.CLOUD_NINE) {
        // PNG textures need different scaling to avoid "zoomed in" appearance
        texture.repeat.set(2, 8);
      } else {
        // JPG textures use original scaling
        texture.repeat.set(1, 4);
      }
    }
  }

  const [colorMap, normalMap, roughnessMap] = textures;

  return (
    <meshStandardMaterial
      key={`texture-${canopyColor}`}
      name={`texture-${canopyColor}`}
      attach="material"
      map={colorMap}
      normalMap={normalMap}
      roughnessMap={roughnessMap}
      side={DoubleSide}
    />
  );
};
